import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, LogOut, Menu, X, CircleDot, Shield, Sun, Moon, Monitor, Code2, Building2, Lock } from "lucide-react";
import { useTheme } from "next-themes";
import { NAV } from "@/config/resources";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { SecurityDialog } from "@/components/SecurityDialog";

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const opts = [
    { key: "light", icon: Sun, label: "Light" },
    { key: "dark", icon: Moon, label: "Dark" },
    { key: "system", icon: Monitor, label: "System" },
  ];
  return (
    <div data-testid="theme-toggle" className="flex items-center gap-1 rounded-lg border border-border bg-muted/40 p-1 mt-2">
      {opts.map((o) => {
        const Icon = o.icon;
        const active = (theme || "system") === o.key;
        return (
          <button key={o.key} type="button" data-testid={`theme-${o.key}`} onClick={() => setTheme(o.key)}
            title={o.label}
            className={`flex-1 flex items-center justify-center gap-1 rounded-md py-1.5 text-xs transition-colors ${
              active ? "bg-indigo-500 text-white" : "text-muted-foreground hover:text-foreground"}`}>
            <Icon className="h-3.5 w-3.5" />
          </button>
        );
      })}
    </div>
  );
}

const ROLE_LABEL = { admin: "Administrator", staff: "Operations Staff", company: "Company Portal", customer: "Customer" };
const ROLE_COLOR = {
  admin: "text-indigo-300 bg-indigo-500/15 border-indigo-500/30",
  staff: "text-cyan-300 bg-cyan-500/15 border-cyan-500/30",
  company: "text-amber-300 bg-amber-500/15 border-amber-500/30",
  customer: "text-emerald-300 bg-emerald-500/15 border-emerald-500/30",
};

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const nav = NAV.filter((n) => n.roles.includes(user.role));

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2.5 px-5 h-16 border-b border-border">
        <div className="h-8 w-8 rounded-lg bg-indigo-500 flex items-center justify-center">
          <ShieldCheck className="h-5 w-5 text-white" />
        </div>
        <div>
          <p className="font-display font-bold leading-none">CITIDAN</p>
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Datacenter</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {nav.map((item) => {
          const Icon = item.icon;
          const active = location.pathname === item.path;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                active
                  ? "bg-indigo-500/15 text-indigo-600 dark:text-white border border-indigo-500/30"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground border border-transparent"
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{item.label}</span>
              {active && <CircleDot className="h-3 w-3 ml-auto text-indigo-400" />}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="flex items-center gap-2 px-2 py-2">
          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-semibold text-foreground">
            {user.name?.slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{user.name}</p>
            <p className="text-xs text-muted-foreground truncate font-mono">{user.email}</p>
          </div>
        </div>
        <ThemeToggle />
        <Button data-testid="logout-button" variant="ghost" onClick={logout}
          className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-accent mt-1">
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </Button>
        <SecurityDialog trigger={
          <Button data-testid="security-button" variant="ghost"
            className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-accent">
            <Shield className="h-4 w-4 mr-2" /> Security · 2FA
          </Button>
        } />
        <NavLink to="/developer" data-testid="nav-developer-portal"
          className="flex items-center w-full rounded-md px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
          <Code2 className="h-4 w-4 mr-2" /> Developer Portal
        </NavLink>
        <NavLink to="/corporate" data-testid="nav-corporate"
          className="flex items-center w-full rounded-md px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
          <Building2 className="h-4 w-4 mr-2" /> Corporate ERP
        </NavLink>
        <NavLink to="/voult" data-testid="nav-voult"
          className="flex items-center w-full rounded-md px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
          <Lock className="h-4 w-4 mr-2" /> Voult Card Vault
        </NavLink>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 w-64 bg-card border-r border-border hidden md:flex flex-col z-30">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 bg-card border-r border-border">
            <SidebarInner />
          </div>
        </div>
      )}

      {/* Topbar */}
      <header className="fixed top-0 right-0 left-0 md:left-64 h-16 z-20 backdrop-blur-xl bg-card/80 border-b border-border flex items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-3">
          <button className="md:hidden text-foreground" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            LIVE LEDGER SYNC
          </div>
        </div>
        <div className={`text-xs font-medium px-3 py-1.5 rounded-full border ${ROLE_COLOR[user.role]}`} data-testid="role-badge">
          {ROLE_LABEL[user.role]}
        </div>
      </header>

      <motion.main
        key={location.pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="md:pl-64 pt-16 min-h-screen"
      >
        <div className="p-4 sm:p-8 max-w-[1600px] mx-auto">{children}</div>
      </motion.main>
    </div>
  );
}
